// Presigned multipart upload manager (Zustand).
// Queue metadata persists to localStorage; file bytes persist to IndexedDB
// (see lib/uploadBlobStore) so uploads survive tab close/reopen. Parts go
// straight to the object store. The store's own record of received parts is the
// source of truth on resume — after a network drop or reload we re-query which
// parts landed (and get fresh URLs) and only send the rest.

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import {
  completeUpload,
  getAsset,
  getUploadStatus,
  initUpload,
  PartUrlExpiredError,
  putPart,
} from '@/api/media'
import { PERSISTED_STORE_KEYS, registerPersistedStore } from '@/stores/persistedKeys'
import { deleteUploadBlob, getUploadBlob, saveUploadBlob } from '@/stores/uploadBlobStore'
import type { MediaAsset, PartUploadTarget } from '@/types/media'

const PART_SIZE = 8 * 1024 * 1024
const PARALLEL_PARTS = 3
const MAX_PART_RETRIES = 4
const PROCESSING_POLL_MS = 3000
const PROCESSING_POLL_MAX = 60

export type UploadItemStatus =
  | 'uploading'
  | 'waiting_network'
  | 'processing'
  | 'done'
  | 'error'
  | 'file_missing'

/** What each upload status is called wherever an upload is shown. */
export const UPLOAD_STATUS_LABELS: Record<UploadItemStatus, string> = {
  uploading: 'Uploading',
  waiting_network: 'Waiting for network',
  processing: 'Processing',
  done: 'Ready',
  error: 'Failed',
  file_missing: 'Needs re-pick',
}

export interface UploadItem {
  upload_id: string
  filename: string
  mime_type: string
  total_size: number
  part_size: number
  parts_total: number
  parts_received: number[]
  status: UploadItemStatus
  progress: number
  error: string | null
  asset: MediaAsset | null
}

interface MediaUploadState {
  uploads: Record<string, UploadItem>
  /** Uploads a chat message is waiting on: its tile shows their progress, so the tray does not. */
  onMessage: Record<string, true>
}

interface MediaUploadActions {
  /** Start each file's upload; their upload ids in order (null for one that could not start). */
  addFiles: (files: File[]) => Promise<(string | null)[]>
  resumePending: () => void
  retryUpload: (uploadId: string) => void
  removeUpload: (uploadId: string) => Promise<void>
  /** Mark an upload as held by a chat message (or hand it back to the tray). */
  holdForMessage: (uploadId: string, held: boolean) => void
}

type MediaUploadStore = MediaUploadState & MediaUploadActions

// In-memory only: uploads currently being pumped (never persisted)
const activePumps = new Set<string>()
// Presigned part URLs expire, so they are never persisted: they come from init
// or the status endpoint and are re-fetched after a reload or a rejected URL.
const partTargets = new Map<string, PartUploadTarget[]>()
// Session cache of picked files — IndexedDB persistence is best-effort, so an
// upload always proceeds from this cache even if the browser rejects IDB blobs
const blobCache = new Map<string, Blob>()

/**
 * Local bytes for an upload picked this session (upload_id doubles as the
 * asset id). Lets the video editor play a clip instantly before the server
 * proxy exists. Callers own the object URL lifecycle.
 */
export function getCachedUploadBlob(uploadId: string): Blob | undefined {
  return blobCache.get(uploadId)
}

function progressFor(received: number, total: number): number {
  return Math.round((received / Math.max(total, 1)) * 100)
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

function waitForOnline(): Promise<void> {
  if (navigator.onLine) return Promise.resolve()
  return new Promise((resolve) => {
    const handler = () => {
      window.removeEventListener('online', handler)
      resolve()
    }
    window.addEventListener('online', handler)
  })
}

export const useMediaUploadStore = create<MediaUploadStore>()(
  devtools(
    persist(
      (set, get) => {
        const patchItem = (uploadId: string, patch: Partial<UploadItem>) => {
          set((state) => {
            const item = state.uploads[uploadId]
            if (!item) return state
            return { uploads: { ...state.uploads, [uploadId]: { ...item, ...patch } } }
          })
        }

        const pollProcessing = async (uploadId: string, assetId: string) => {
          for (let attempt = 0; attempt < PROCESSING_POLL_MAX; attempt++) {
            await sleep(PROCESSING_POLL_MS)
            const item = get().uploads[uploadId]
            if (!item || item.status !== 'processing') return
            try {
              const asset = await getAsset(assetId)
              if (asset.status === 'ready') {
                patchItem(uploadId, { status: 'done', asset, progress: 100 })
                return
              }
              if (asset.status === 'failed') {
                patchItem(uploadId, {
                  status: 'error',
                  asset,
                  error: 'Processing failed',
                })
                return
              }
            } catch {
              // transient — keep polling
            }
          }
        }

        const finalize = async (uploadId: string) => {
          const asset = await completeUpload(uploadId)
          blobCache.delete(uploadId)
          await deleteUploadBlob(uploadId).catch(() => undefined)
          if (asset.status === 'ready') {
            patchItem(uploadId, { status: 'done', asset, progress: 100 })
          } else {
            patchItem(uploadId, { status: 'processing', asset, progress: 100 })
            void pollProcessing(uploadId, asset.id)
          }
        }

        // Server state is the source of truth: which parts the store holds, plus
        // fresh presigned URLs for the rest.
        const refreshTargets = async (uploadId: string) => {
          const status = await getUploadStatus(uploadId)
          partTargets.set(uploadId, status.parts)
          patchItem(uploadId, {
            parts_received: status.parts_received,
            progress: progressFor(status.parts_received.length, status.parts_total),
          })
        }

        const pump = async (uploadId: string) => {
          if (activePumps.has(uploadId)) return
          activePumps.add(uploadId)
          try {
            for (;;) {
              const item = get().uploads[uploadId]
              if (!item || (item.status !== 'uploading' && item.status !== 'waiting_network')) {
                return
              }

              if (!navigator.onLine) {
                patchItem(uploadId, { status: 'waiting_network' })
                await waitForOnline()
                // Server state is the source of truth after a drop
                await refreshTargets(uploadId)
                patchItem(uploadId, { status: 'uploading' })
                continue
              }

              const blob =
                blobCache.get(uploadId) ?? (await getUploadBlob(uploadId).catch(() => undefined))
              if (!blob) {
                patchItem(uploadId, {
                  status: 'file_missing',
                  error: 'File no longer available — pick it again to resume',
                })
                return
              }

              const current = get().uploads[uploadId]
              if (!current) return
              const received = new Set(current.parts_received)
              if (received.size >= current.parts_total) {
                await finalize(uploadId)
                return
              }

              if (!partTargets.has(uploadId)) await refreshTargets(uploadId)
              const queue = (partTargets.get(uploadId) ?? []).filter(
                (target) => !received.has(target.part_number)
              )

              let failures = 0
              const worker = async () => {
                for (;;) {
                  const target = queue.shift()
                  if (target === undefined) return
                  const start = (target.part_number - 1) * current.part_size
                  const part = blob.slice(start, Math.min(start + current.part_size, blob.size))
                  let url = target.url
                  let uploaded = false
                  for (let retry = 0; retry < MAX_PART_RETRIES && !uploaded; retry++) {
                    try {
                      await putPart(url, part)
                      const latest = get().uploads[uploadId]
                      if (latest) {
                        const parts_received = [...latest.parts_received, target.part_number]
                        patchItem(uploadId, {
                          parts_received,
                          progress: progressFor(parts_received.length, latest.parts_total),
                        })
                      }
                      uploaded = true
                    } catch (error) {
                      if (!navigator.onLine) return // pump loop handles reconnect
                      if (error instanceof PartUrlExpiredError) {
                        await refreshTargets(uploadId)
                        url =
                          partTargets
                            .get(uploadId)
                            ?.find((t) => t.part_number === target.part_number)?.url ?? url
                      } else {
                        await sleep(1000 * 2 ** retry)
                      }
                    }
                  }
                  if (!uploaded) failures++
                }
              }
              await Promise.all(
                Array.from({ length: Math.min(PARALLEL_PARTS, queue.length) }, worker)
              )

              if (failures > 0 && navigator.onLine) {
                patchItem(uploadId, {
                  status: 'error',
                  error: 'Upload failed after retries — tap retry',
                })
                return
              }
              // Loop again: either finalize (all sent) or wait for reconnect
            }
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload failed'
            patchItem(uploadId, { status: 'error', error: message })
          } finally {
            activePumps.delete(uploadId)
          }
        }

        return {
          uploads: {},
          onMessage: {},

          addFiles: async (files: File[]) => {
            const started: (string | null)[] = []
            for (const file of files) {
              started.push(null)
              try {
                const init = await initUpload({
                  filename: file.name,
                  content_type: file.type || 'application/octet-stream',
                  total_size: file.size,
                  part_size: PART_SIZE,
                })

                if (init.deduplicated && init.asset) {
                  set((state) => ({
                    uploads: {
                      ...state.uploads,
                      [init.upload_id]: {
                        upload_id: init.upload_id,
                        filename: file.name,
                        mime_type: file.type,
                        total_size: file.size,
                        part_size: init.part_size,
                        parts_total: init.parts_total,
                        parts_received: init.parts_received,
                        status: 'done',
                        progress: 100,
                        error: null,
                        asset: init.asset,
                      },
                    },
                  }))
                  started[started.length - 1] = init.upload_id
                  continue
                }

                partTargets.set(init.upload_id, init.parts)
                blobCache.set(init.upload_id, file)
                // Best-effort durable copy for tab-close resume; some WebKit
                // builds reject file data in IndexedDB — the upload still runs
                // from the in-memory cache.
                await saveUploadBlob(init.upload_id, file).catch(() => undefined)
                set((state) => ({
                  uploads: {
                    ...state.uploads,
                    [init.upload_id]: {
                      upload_id: init.upload_id,
                      filename: file.name,
                      mime_type: file.type,
                      total_size: file.size,
                      part_size: init.part_size,
                      parts_total: init.parts_total,
                      parts_received: [],
                      status: 'uploading',
                      progress: 0,
                      error: null,
                      asset: null,
                    },
                  },
                }))
                void pump(init.upload_id)
                started[started.length - 1] = init.upload_id
              } catch (error) {
                console.error('Failed to start upload', file.name, error)
              }
            }
            return started
          },

          resumePending: () => {
            const { uploads } = get()
            for (const item of Object.values(uploads)) {
              if (item.status === 'uploading' || item.status === 'waiting_network') {
                void (async () => {
                  try {
                    const status = await getUploadStatus(item.upload_id)
                    if (status.status !== 'uploading') {
                      // Completed elsewhere (or already processing) — reflect it
                      if (status.status === 'ready') {
                        patchItem(item.upload_id, {
                          status: 'done',
                          asset: status.asset,
                          progress: 100,
                        })
                      } else if (status.status === 'failed') {
                        patchItem(item.upload_id, {
                          status: 'error',
                          asset: status.asset,
                          error: 'Processing failed',
                        })
                      } else {
                        patchItem(item.upload_id, {
                          status: 'processing',
                          asset: status.asset,
                          progress: 100,
                        })
                        void pollProcessing(item.upload_id, status.asset.id)
                      }
                      void deleteUploadBlob(item.upload_id)
                      return
                    }
                    partTargets.set(item.upload_id, status.parts)
                    patchItem(item.upload_id, {
                      status: 'uploading',
                      parts_received: status.parts_received,
                      progress: progressFor(status.parts_received.length, status.parts_total),
                    })
                    void pump(item.upload_id)
                  } catch {
                    patchItem(item.upload_id, {
                      status: 'error',
                      error: 'Could not reach the server to resume',
                    })
                  }
                })()
              } else if (item.status === 'processing' && item.asset) {
                void pollProcessing(item.upload_id, item.asset.id)
              }
            }
          },

          retryUpload: (uploadId: string) => {
            const item = get().uploads[uploadId]
            if (!item) return
            patchItem(uploadId, { status: 'uploading', error: null })
            void pump(uploadId)
          },

          removeUpload: async (uploadId: string) => {
            blobCache.delete(uploadId)
            partTargets.delete(uploadId)
            await deleteUploadBlob(uploadId).catch(() => undefined)
            set((state) => {
              const uploads = { ...state.uploads }
              delete uploads[uploadId]
              const onMessage = { ...state.onMessage }
              delete onMessage[uploadId]
              return { uploads, onMessage }
            })
          },

          holdForMessage: (uploadId: string, held: boolean) => {
            set((state) => {
              const onMessage = { ...state.onMessage }
              if (held) onMessage[uploadId] = true
              else delete onMessage[uploadId]
              return { onMessage }
            })
          },
        }
      },
      {
        name: PERSISTED_STORE_KEYS.mediaUpload,
        partialize: (state) => ({ uploads: state.uploads }),
      }
    ),
    { name: 'MediaUploadStore', enabled: import.meta.env.DEV }
  )
)

// Filenames and upload progress from the previous account must not survive a
// logout or a business switch (#1923). Dropping the queue also strands the
// in-session blob cache, so clear that in the same step.
registerPersistedStore(PERSISTED_STORE_KEYS.mediaUpload, () => {
  blobCache.clear()
  partTargets.clear()
  useMediaUploadStore.setState({ uploads: {}, onMessage: {} })
})

// Resume waiting uploads as soon as connectivity returns, wherever the user is
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => {
    useMediaUploadStore.getState().resumePending()
  })
}
